import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import {
  type GetInterviewsResponse,
  getInterviewsResponseSchema,
  interviewsApiUrl,
} from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { minutesToMilliseconds } from "date-fns";

/**
 * Hook to check if a worker has booked at least one interview before.
 * This checks for ANY interview record regardless of status (Pending, Booked, Cancelled,
 * Rescheduled, AttendanceConfirmed, NoCallNoShow, etc.).
 *
 * This is used to determine if a worker is "returning" (has gone through the booking
 * process before) vs "new" (first time booking).
 *
 * This checks across ALL placements for the worker, not just a specific placement.
 */
export function useHasBookedInterview(options?: UseGetQueryOptions<GetInterviewsResponse>) {
  const { data, isLoading, isError } = useGetQuery({
    url: interviewsApiUrl,
    queryParams: {
      page: {
        size: 1, // Only need to check if at least one exists
      },
      filter: {},
    },
    responseSchema: getInterviewsResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_WORKER_INTERVIEW_HISTORY_FAILURE,
    },
    staleTime: minutesToMilliseconds(5), // 5 minutes cache
    ...options,
  });

  const hasBookedInterview = (data?.data?.length ?? 0) > 0;

  return {
    hasBookedInterview,
    isLoading,
    isError,
  };
}
